import { useState } from 'react';
import { Search, Filter, X, Bath } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Slider } from './ui/slider';
import { Property, PropertyFilters } from '../types/property';

interface PropertyFiltersProps {
  filters: PropertyFilters;
  onFiltersChange: (filters: PropertyFilters) => void;
  resultsCount: number;
}

// Spec v2.1 status colors
const STATUS_BADGE_STYLES: Record<string, { active: string; dot: string }> = {
  Active:    { active: 'bg-[#10B981] hover:bg-[#10B981]/90 text-white border-transparent', dot: 'bg-[#10B981]' },
  Pending:   { active: 'bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-black border-transparent', dot: 'bg-[#F59E0B]' },
  Sold:      { active: 'bg-[#EF4444] hover:bg-[#EF4444]/90 text-white border-transparent', dot: 'bg-[#EF4444]' },
  Withdrawn: { active: 'bg-[#6B7280] hover:bg-[#6B7280]/90 text-white border-transparent', dot: 'bg-[#6B7280]' },
};

export function PropertyFilters({ filters, onFiltersChange, resultsCount }: PropertyFiltersProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [priceRange, setPriceRange] = useState([
    filters.minPrice || 0,
    filters.maxPrice || 5000000
  ]);

  const propertyTypes: Property['type'][] = ['House', 'Apartment', 'Townhouse', 'Farm', 'VacantLand', 'Commercial'];

  // Human-friendly labels for property types
  const typeLabels: Record<string, string> = {
    House: 'House',
    Apartment: 'Apartment',
    Townhouse: 'Townhouse',
    Farm: 'Farm',
    VacantLand: 'Vacant Land',
    Commercial: 'Commercial',
  };

  const bedroomOptions = [1, 2, 3, 4, 5];
  const bathroomOptions = [1, 2, 3, 4, 5];
  const listingStatuses = ['Active', 'Pending', 'Sold', 'Rented', 'Expired', 'Withdrawn', 'Cancelled'] as const;
  const quickFilterStatuses = ['Active', 'Pending', 'Sold', 'Withdrawn'] as const;
  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'price_asc', label: 'Price: Low to High' },
    { value: 'price_desc', label: 'Price: High to Low' },
    { value: 'bedrooms_desc', label: 'Most Bedrooms' },
    { value: 'bathrooms_desc', label: 'Most Bathrooms' },
  ];

  const updateFilter = (key: keyof PropertyFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const clearFilters = () => {
    onFiltersChange({});
    setPriceRange([0, 5000000]);
  };

  const applyPriceRange = () => {
    onFiltersChange({
      ...filters,
      minPrice: priceRange[0],
      maxPrice: priceRange[1],
    });
  };

  const formatPrice = (price: number) => {
    if (price >= 1000000) {
      return `R${(price / 1000000).toFixed(1)}M`;
    }
    return `R${price.toLocaleString()}`;
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== undefined && v !== '').length;

  return (
    <div className="space-y-4">
      {/* Main Search Bar */}
      <Card className="p-4 bg-card/60 backdrop-blur-md border-border/50">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search by suburb, city, or property title..."
              value={filters.search || ''}
              onChange={(e) => updateFilter('search', e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2"
              data-testid="property-filters-toggle-button"
            >
              <Filter className="w-4 h-4" />
              Filters
              {activeFiltersCount > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs bg-primary/20 text-primary">
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>

            {activeFiltersCount > 0 && (
              <Button variant="outline" onClick={clearFilters} size="sm" data-testid="property-filters-clear-button">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Quick Filters */}
        <div className="flex flex-wrap gap-2 mt-3">
          {propertyTypes.map((type) => (
            <Badge
              key={type}
              variant={filters.type === type ? "default" : "outline"}
              className="cursor-pointer transition-all duration-200 hover:scale-105"
              onClick={() => updateFilter('type', filters.type === type ? undefined : type)}
            >
              {typeLabels[type] || type}
            </Badge>
          ))}

        </div>
      </Card>

      {/* Advanced Filters */}
      {showAdvanced && (
        <Card className="p-4 bg-card/60 backdrop-blur-md border-border/50">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Location */}
            <div className="space-y-2">
              <Label>City</Label>
              <Select value={filters.city || '__all__'} onValueChange={(value) => updateFilter('city', value === '__all__' ? undefined : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Any city" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Any city</SelectItem>
                  {[].map((city) => (
                    <SelectItem key={city} value={city}>{city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Suburb</Label>
              <Select value={filters.suburb || '__all__'} onValueChange={(value) => updateFilter('suburb', value === '__all__' ? undefined : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Any suburb" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Any suburb</SelectItem>
                  {[].map((suburb) => (
                    <SelectItem key={suburb} value={suburb}>{suburb}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bedrooms */}
            <div className="space-y-2">
              <Label>Bedrooms</Label>
              <Select value={filters.bedrooms?.toString() || '__all__'} onValueChange={(value) => updateFilter('bedrooms', value === '__all__' ? undefined : parseInt(value))}>
                <SelectTrigger>
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Any</SelectItem>
                  {bedroomOptions.map((beds) => (
                    <SelectItem key={beds} value={beds.toString()}>{beds}+ beds</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bathrooms */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Bath className="w-3.5 h-3.5 text-primary/70" />
                Bathrooms
              </Label>
              <Select value={filters.bathrooms?.toString() || '__all__'} onValueChange={(value) => updateFilter('bathrooms', value === '__all__' ? undefined : parseInt(value))}>
                <SelectTrigger>
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Any</SelectItem>
                  {bathroomOptions.map((baths) => (
                    <SelectItem key={baths} value={baths.toString()}>{baths}+ baths</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Listing Status */}
            <div className="space-y-2">
              <Label>Listing Status</Label>
              <Select value={filters.listingStatus || '__all__'} onValueChange={(value) => updateFilter('listingStatus', value === '__all__' ? undefined : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Any status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Any status</SelectItem>
                  {listingStatuses.map((status) => (
                    <SelectItem key={status} value={status}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          STATUS_BADGE_STYLES[status]?.dot || 'bg-gray-400'
                        }`} />
                        {status}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sort */}
            <div className="space-y-2">
              <Label>Sort by</Label>
              <Select value={filters.sort || 'newest'} onValueChange={(value: any) => updateFilter('sort', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Price Range */}
          <div className="mt-6 space-y-3">
            <Label>Price Range</Label>
            <div className="px-3">
              <Slider
                value={priceRange}
                onValueChange={setPriceRange}
                max={5000000}
                min={0}
                step={100000}
                className="w-full"
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {formatPrice(priceRange[0])} - {formatPrice(priceRange[1])}
              </span>
              <Button size="sm" onClick={applyPriceRange} data-testid="property-filters-apply-price-button">
                Apply
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Results Count */}
      <div className="flex justify-between items-center text-sm text-muted-foreground">
        <span>{resultsCount} properties found</span>
        {activeFiltersCount > 0 && (
          <button 
            onClick={clearFilters}
            className="text-primary/80 hover:text-primary text-xs underline underline-offset-2"
          >
            Clear all filters
          </button>
        )}
      </div>
    </div>
  );
}